import { useMemo } from 'react';

import { TestStatus } from '@dbt-labs/dbt-dag';

import {
  DatasetField,
  ModelAppliedStateNode,
  ModelLevelConstraint,
  TableNode,
  ViewNode,
} from '@/__generated__/graphql';
import { ParsedTestName, parseTestName, TestNameData } from '@/util/dbt/testName';
import { RequestedModelAppliedNode } from './discovery/model';
import { RequestedSeedAppliedNode } from './discovery/seed';
import { RequestedSnapshotAppliedNode } from './discovery/snapshot';
import { RequestedSourceAppliedNode } from './discovery/source';
import { useModelConstraints } from './useModelConstraints';

type ColumnMap = Record<string, string[] | undefined>;

export type ColumnTest = {
  uniqueId: string;
  name: ParsedTestName;
  status: TestStatus | null;
  values?: string[];
};

export type AppliedColumn = {
  name: string | null;
  type: string | null;
  detail?: string | null;
  description: string | null;
  descriptionOriginColumnName: string | null;
  descriptionOriginResourceUniqueId: string | null;
  meta?: Record<string, any>;
  metaString?: string;
  tests: ColumnTest[];
  constraints: ModelLevelConstraint[] | null;
};

type TestData = TestNameData & {
  executionInfo: {
    lastRunStatus: TestStatus | null;
    executeCompletedAt: string | null;
  } | null;
};

export type ResourceWithColumns =
  | RequestedModelAppliedNode
  | RequestedSnapshotAppliedNode
  | RequestedSourceAppliedNode
  | RequestedSeedAppliedNode;

export type AssetWithColumns = TableNode | ViewNode;

const convertDatasetFieldToAppliedColumn = (field: DatasetField): AppliedColumn => {
  return {
    name: field.fieldPath,
    type: field.nativeDataType,
    detail: field.nullable ? 'Nullable' : 'Not Nullable',
    description: field.description,
    descriptionOriginColumnName: null,
    descriptionOriginResourceUniqueId: null,
    meta: undefined,
    metaString: undefined,
    tests: [],
    constraints: [],
  };
};

export const useAppliedColumns = (
  resource: ResourceWithColumns | AssetWithColumns | null | undefined,
): AppliedColumn[] | undefined => {
  const hasResponse = !!resource;
  const constraints = useModelConstraints(resource as ModelAppliedStateNode);

  return useMemo(() => {
    if (!hasResponse) return undefined;

    // handle dbt resources (models, seeds, snapshots, sources)
    if (resource && 'catalog' in resource) {
      const columnsData = resource.catalog?.columns;
      if (!columnsData) return [];

      const testData = resource.tests || [];

      const columnToTestIds = testData.reduce((acc: ColumnMap, test: any) => {
        if (!test.columnName) return acc;
        const columnName: string = test.columnName.toLowerCase();
        acc[columnName] ??= [];
        acc[columnName]!.push(test.uniqueId as string);
        return acc;
      }, {});

      const testMap = testData.reduce(
        (acc: Record<string, TestData | undefined>, test: any) => {
          acc[test.uniqueId] = test as TestData;
          return acc;
        },
        {},
      );

      const columnsWithTests: AppliedColumn[] = [];
      columnsData.forEach((column) => {
        if (!column) return;
        const {
          name,
          type,
          description,
          comment,
          meta,
          descriptionOriginColumnName,
          descriptionOriginResourceUniqueId,
        } = column;
        if (!name || !type) return;

        const tests: ColumnTest[] = [];
        (testData.length > 0 ? columnToTestIds[name.toLowerCase()] || [] : []).forEach(
          (testUniqueId: string) => {
            const test = testMap[testUniqueId];
            const name = test ? parseTestName(test) : null;
            if (name) {
              tests.push({
                name,
                uniqueId: testUniqueId,
                status: test?.executionInfo?.lastRunStatus ?? null,
                values: test?.testMetadata?.kwargs?.values ?? undefined,
              });
            }
          },
        );

        columnsWithTests.push({
          name,
          type,
          detail: null,
          description: (description || comment) ?? null,
          descriptionOriginColumnName,
          descriptionOriginResourceUniqueId,
          meta,
          metaString: JSON.stringify(meta),
          tests,
          constraints: constraints?.[name] ?? [],
        });
      });
      return columnsWithTests;
    }

    // handle warehouse assets (tables, views)
    if (resource && 'fields' in resource) {
      if (!resource.fields) return [];
      return resource.fields.map(convertDatasetFieldToAppliedColumn);
    }

    return undefined;
  }, [resource, hasResponse, constraints]);
};
